import { Chart, PointerEventObject } from 'highcharts/highstock';

import {
    HighlightAnnotation,
    HorizontalLineAnnotation,
    LineSegmentAnnotation,
    TextBubbleAnnotation,
    VerticalLineAnnotation
} from '../../models/annotation';
import { updateTooltip } from '../../models/highstock-formatters';
import { MouseMode } from '../../models/mouse-mode';
import { EditCommentBubbleDialogComponent } from '../edit-comment-dialog/edit-comment-dialog.component';

import { LineChartComponent } from './line-chart.component';


/**
 * Handles mousedown on the chart, must be called as onMouseDown( this, event ) where
 * 'this' is the line-chart.component controller
 */
export function onMouseDown( this: LineChartComponent, event: PointerEvent ) {
    const coords = this.chart.pointer.getCoordinates( this.chart.pointer.normalize( event ) );
    const point = {
        x: coords.xAxis[0].value,
        y: coords.yAxis[0].value
    };

    if ( mouseIsOverChartArea( event, this.chart ) ) {
        this.annotationState.dragStart = point;
    }
}

/**
 * Handles mouseup on the chart, must be called as onMouseDown( this, event ) where
 * 'this' is the line-chart.component controller
 */
export function onMouseUp( this: LineChartComponent, event: PointerEvent ) {
    const coords = this.chart.pointer.getCoordinates( this.chart.pointer.normalize( event ) );
    const point = {
        x: coords.xAxis[0].value,
        y: coords.yAxis[0].value
    };
    let newAnnotation;

    if ( mouseIsOverChartArea( event, this.chart ) ) {
        this.annotationState.dragEnd = point;
        this.annotationState.currentAnnotation = null;
        const color = this.mouseModeService.getAnnotationColor();

        if ( this.currentMouseMode === MouseMode.DRAW_LINE_X ) {
            newAnnotation = new VerticalLineAnnotation(
                'type-line' + ' ' + this.mouseModeService.getLineType(),
                color,
                point.x
            );
        } else if ( this.currentMouseMode === MouseMode.DRAW_LINE_Y ) {
            newAnnotation = new HorizontalLineAnnotation(
                'type-line' + ' ' + this.mouseModeService.getLineType(),
                color,
                point.y
            );
        } else if ( this.currentMouseMode === MouseMode.ADD_TEXT_BUBBLE ) {
            // if we are clicking on an existing bubble, edit the existing one instead of creating a new one
            const clickedTextBubble = eventIsOnTextBubbleAnnotation( event, this.chart );
            if ( clickedTextBubble ) {
                this.matDialog.open( EditCommentBubbleDialogComponent, {
                    data: clickedTextBubble.options.labels[0].text
                }).afterClosed().subscribe( result => {
                    if ( result ) {
                        clickedTextBubble.update({
                            labels: {
                                text: result
                            }
                        });
                    }
                });
            } else {
                this.matDialog.open( EditCommentBubbleDialogComponent ).afterClosed().subscribe( result => {
                    if ( result ) {
                        this.annotationsService.addAnnotation(
                            this.plotId,
                            new TextBubbleAnnotation( result, point.x, point.y )
                        );
                    }
                });
            }
        }
    }

    if ( newAnnotation != null ) {
        this.annotationsService.addAnnotation( this.plotId, newAnnotation );
    }
    this.annotationState.dragStart = null;
}

export function mouseIsOverChartArea( mouseEvent: MouseEvent, chart: Chart ) {
    // Use the position of the container's bounding box, the "plotBox", and the mouse coordinates
    // to determine if the mouse is over the chart area
    const boundingRect = chart.container.getBoundingClientRect();
    // calculate the position of the mouse relative to the highcharts container
    const containerMousePos = {
        x: mouseEvent.clientX - boundingRect.left,
        y: mouseEvent.clientY - boundingRect.top
    };

    const plotBox = chart['plotBox'];
    return ( containerMousePos.x >= plotBox.x && containerMousePos.x <= plotBox.x + plotBox.width )
        && ( containerMousePos.y >= plotBox.y && containerMousePos.y <= plotBox.y + plotBox.height );
}

/**
 * Takes a mouse event. If the event target is within a highcharts annotation label element, it returns
 * that element. Otherwise, it returns false.
 */
export function clickedOnHighchartsLabel( e ) {
    // Highstock API doesn't provide a way to add click event handlers to annotations, so we must
    // check if the label was clicked on by inspecting the chart click's event target.
    let currentElement = e.target;
    while ( currentElement && currentElement !== document.body ) {
        if ( currentElement.classList && currentElement.classList.contains('highcharts-annotation-labels') ) {
            return currentElement;
        }
        currentElement = currentElement.parentElement || currentElement.parentNode;
    }
    return false;
}

/**
 * Takes a mouse event. If the coordinates of the event correspond with a text bubble annotation,
 * returns that annotation (from the list at scope.frameScope.annotations).
 * Otherwise, returns false.
 */
export function eventIsOnTextBubbleAnnotation( e, chart: Chart ) {
    const labelElement = clickedOnHighchartsLabel( e );
    if ( labelElement ) {
        // find the annotation object on the highchart object which has the clicked element
        const clickedHighchartsAnnotation = chart['annotations'].find( highchartsAnnotation => {
            return highchartsAnnotation.labelsGroup.element === labelElement;
        });
        // find the element in the frame's annotations array which corresponds to this highchart annotation object
        const textBubbleAnnotation = chart['annotations'].find( annotation => {
            return annotation.options.id === clickedHighchartsAnnotation.options.id;
        });
        return textBubbleAnnotation || false;
    } else {
        return false;
    }
}

/**
 * Handles mousemove on the chart, must be called as onMouseMove( this, event ) where
 * 'this' is the line-chart.component controller
 */
export function onMouseMove( this: LineChartComponent, event: PointerEvent ) {
    if ( this.chart == null ) {
        return;
    }
    const coords = this.chart.pointer.getCoordinates( this.chart.pointer.normalize( event ) );
    const point = {
        x: coords.xAxis[0].value,
        y: coords.yAxis[0].value
    };

    this.zone.runOutsideAngular( () => {
        const pointerPosition = this.chart.pointer.getCoordinates( event as PointerEventObject ).xAxis[0].value;
        this.plotsService.setXPosition( pointerPosition, this.plotId );
        if ( this.menuOptions.legend ) {
            updateTooltip( this.chart, true, this.showSingleLegendXValue );
        }
    });

    // Highcharts has an option to set a cursor for the whole plot: chart area, axes, labels,
    // padding, and all. We want the ability to set the cursor only over the chart area (where
    // series can possibly be drawn). Setting a :hover CSS rule on the chart area background
    // doesn't work, because other elements are drawn on top of the background. A way around
    // this is to set a CSS class on the chart's container element only when the mouse is over
    // the chart area.

    // add or remove a CSS class based on the hover state
    if ( mouseIsOverChartArea( event, this.chart ) ) {
        this.chart.container.classList.add( 'mouse-over-chart' );

        let newAnnotation;
        this.annotationState.dragEnd = point;
        const color = this.mouseModeService.getAnnotationColor();

        if ( this.currentMouseMode === MouseMode.DRAW_AREA_X && this.annotationState.dragStart != null ) {
            newAnnotation = new HighlightAnnotation(
                color,
                this.annotationState.dragStart.x,
                this.annotationState.dragEnd.x
            );
        }

        if ( this.currentMouseMode === MouseMode.DRAW_LINE_SEGMENT && this.annotationState.dragStart != null ) {
            newAnnotation = new LineSegmentAnnotation(
                'type-line' + ' ' + this.mouseModeService.getLineType(),
                color,
                this.annotationState.dragStart.x,
                this.annotationState.dragEnd.x,
                this.annotationState.dragStart.y,
                this.annotationState.dragEnd.y
            );
        }

        if ( this.annotationState.currentAnnotation != null ) {
            this.annotationsService.removeAnnotation( this.plotId, this.annotationState.currentAnnotation.id );
        }
        if ( newAnnotation != null ) {
            this.annotationsService.addAnnotation( this.plotId, newAnnotation );
            this.annotationState.currentAnnotation = newAnnotation;
        }
    } else {
        this.chart.container.classList.remove( 'mouse-over-chart' );
    }
}
